import { HDate, months } from '@hebcal/core';

/**
 * Pirkei Avot being studied on Shabbat between Pesach and Rosh Hashana
 *
 * "From at least the time of Saadia Gaon (10th century),
 * it has been customary to study one chapter a week on each Shabbat
 * between Passover and Shavuot; today, the tractate is generally studied
 * on each Shabbat of the summer, from Passover to Rosh Hashanah,
 * the entire cycle repeating a few times with doubling of chapters
 * at the end if there are not a perfect multiple of six weeks"
 * https://en.wikipedia.org/wiki/Pirkei_Avot#Study_of_the_work
 *
 * @param {Date|HDate} dt - The date to check
 * @returns {number[]|null} - Array of chapter numbers (since it can return 2 chapters) or null if no Pirkei Avot on that day
 */
export function pirkeiAvot(dt) {
  const hd = new HDate(dt);
  if (hd.getDay() !== 6) {
    // Not Saturday
    return null;
  }
  const shvi = new HDate(21, months.NISAN, hd.getFullYear());
  if (hd.abs() <= shvi.abs()) {
    return null; // before Pesach
  }
  const first = shvi.after(6);
  let weekDiff = Math.ceil(hd.deltaDays(first) / 7);
  // when a Holiday falls on Saturday, Pirkei avot is not studied, same is for erev Tisha B'av
  const holidays = [];
  // Outside Israel
  holidays.push(shvi.next()); // 8th day Pesach
  holidays.push(new HDate(7, months.SIVAN, hd.getFullYear())); // 2nd day Shavuot
  
  const erev9av = new HDate(8, months.AV, hd.getFullYear());
  holidays.push(erev9av); // Erev Tish'a B'Av
  holidays.push(erev9av.next()); // Tish'a B'Av
  for (const day of holidays) {
    if (day.isSameDate(hd)) {
      // if dt is one of the holidays return nothing (null)
      return null;
    }
    if (day.deltaDays(hd) <= 0 && day.getDay() === 6) {
      weekDiff -= 1; // if dt is past that holiday don't count that week
    }
  }
  if (weekDiff < 18) {
    return [(weekDiff % 6) + 1];
  }
  // fourth round is different
  const rh = new HDate(1, months.TISHREI, hd.getFullYear() + 1);
  const last = rh.before(6);
  const weeksRemain = Math.ceil(last.deltaDays(hd) / 7);
  switch (weeksRemain) {
    case 0:
      return [5, 6];
    case 1:
      return [3, 4];
    case 2:
      return weekDiff % 6 === 1 ? [2] : [1, 2];
    case 3:
      return [1];
  }
  return null;
}

/**
 * Converts a number to its Hebrew numeral representation
 * @param {number} num - The number to convert
 * @returns {string} - The Hebrew numeral
 */
function getHebrewNumeral(num) {
  const hebrewNumerals = ['א', 'ב', 'ג', 'ד', 'ה', 'ו'];
  return hebrewNumerals[num - 1] || '';
}

/**
 * Gets the Pirkei Avot chapter(s) for a given date in Hebrew numerals
 * @param {Date|HDate} date - The date to check
 * @returns {string|null} - The chapter(s) in Hebrew numerals (e.g., "א", "א-ב") or null if not applicable
 */
export function getPirkeiAvotPerek(date) {
  const chapters = pirkeiAvot(date);
  
  if (!chapters || chapters.length === 0) {
    return null;
  }
  
  if (chapters.length === 1) {
    return getHebrewNumeral(chapters[0]);
  } else {
    return `${getHebrewNumeral(chapters[0])}-${getHebrewNumeral(chapters[1])}`;
  }
}
